import { minBy } from 'lodash';
import { squaredDistance } from '../../utils/math';
import { SECOND } from '../../utils/time';
import { ServerWave } from '../../wave/types/wave.type';
import {
  ClusterBeat,
  ClusterType,
  ServerCluster,
  SortingStrategy,
} from '../clustering.types';

/**
 * We add 600 points before and after the beat which
 */
export const SIDE_POINTS = 600;

// Size of the signal
export const MV_INIT = 1.0; // +/- 1.0mV

export const CLUSTER_CODES: { [key: string]: ClusterType } = {
  CL_qrs_morpho_16: 'Unclassified',
  CL_auto_sv_1: 'PAC',
  CL_auto_v_1: 'PVC',
  CL_rh_sinus_1: 'Sinus',
  CL_tech_11: 'Noise',
};

export const getServerCode = (code: ClusterType) =>
  Object.keys(CLUSTER_CODES).find(
    (clusterCode) => CLUSTER_CODES[clusterCode] === code,
  ) as string;

export const filterCategories = (serverCluster: ServerCluster) =>
  Object.keys(CLUSTER_CODES).includes(serverCluster.category_code);

export const jsonToBeats = (
  beats: [number, number, number, number][],
): ClusterBeat[] =>
  beats.map((beat) => ({
    onset: Math.round(beat[0] * SECOND),
    offset: Math.round(beat[1] * SECOND),
    // TODO: Remove hack once delin team is done
    originalOffset: beat[1],
    id: beat[2],
    data: [],
  }));

export const beatsToServerOnsetsOffsets = (beats: ClusterBeat[]) =>
  // TODO: Remove hack once delin team is done
  beats.map((beat) => [
    beat.onset / SECOND,
    beat.originalOffset ? beat.originalOffset : beat.offset / SECOND,
    beat.id,
    0,
  ]);

/**
 * Shift the onset to 0 and all the rest of the points the same
 * distance, so the clusters can be more centered
 * @param data
 * @param onset
 * @returns a numeric array with the shifted data
 */
export const shiftToCenter = (
  data: number[],
  mvPerUnit: number,
  onsetIndex: number,
) => {
  const shiftValue = data[onsetIndex] ?? 0; // the onset point
  // is right after the first 600 points
  return data.map((value) => (value - shiftValue) * mvPerUnit);
};

/**
 * Calculate the duration that corresponds to a specific number of points in a holter signal.
 * The convention in cardiolib is 250 points every second (1000ms)
 * @param points
 * @returns a number in ms
 */
export const getDurationFromNumberOfPoints = (points: number) =>
  // TODO: it's always 250 but this should be changed to the cluster frequency
  (SECOND * points) / 250;

export const getNumberOfPointsFromDuration = (duration: number) =>
  Math.round((duration * 250) / SECOND);

/**
 * Every data array has to have a size of targetSize * 2
 * Here we fill with null values the side points that don't have a value
 * @param data the array of points
 * @param beat the beat
 * @param targetSize the points that we add on the left and right of
 * the beat
 * @param maxDurationLimit the the duration of the ecg
 * @returns
 */
export const handleEdgeBeats = (
  data: number[],
  beat: ClusterBeat,
  targetSize: number,
  maxDurationLimit: number,
) => {
  if (data.length >= targetSize * 2) {
    return data;
  }

  const addedDuration = getDurationFromNumberOfPoints(targetSize);
  const windowStart = beat.onset - addedDuration;
  const windowEnd = beat.onset + addedDuration;
  const newData: (number | null)[] = [...data];
  // there is no way the holter will be less than 4.8 seconds that we are trying to show,
  // so the beat will either be at the beginning or the end, no reason to check for both cases
  if (windowStart < 0) {
    const points = getNumberOfPointsFromDuration(-windowStart);
    const emptyData = Array(points).fill(null);
    newData.unshift(...emptyData);
  } else if (windowEnd > maxDurationLimit) {
    const points = getNumberOfPointsFromDuration(windowEnd - maxDurationLimit);
    const emptyData = Array(points).fill(null);
    newData.push(...emptyData);
  }
  return newData;
};

/**
 * Add the time equivalent to 600 points before and after each onset
 * @param clusterBeats
 * @param maxLimit the duration of the ecg, and the end on the request
 * should not exceed that limit, otherwise the request fails
 * @returns a string with all the duration spans for each strip of the cluster
 */
export const getDurationBatches = (
  clusterBeats: ClusterBeat[],
  maxLimit: number,
) => {
  const addedDuration = getDurationFromNumberOfPoints(SIDE_POINTS);

  return clusterBeats
    .map((beat) => {
      const beforeOnset = Math.round(beat.onset - addedDuration);
      const afterOnset = Math.round(beat.onset + addedDuration);
      const start = beforeOnset > 0 ? beforeOnset : 0;
      const end = afterOnset <= maxLimit ? afterOnset : maxLimit;
      return `&ts[]=${start},${end},`;
    })
    .join('');
};

export const getNeighborRRs = (beat: ClusterBeat, waves: ServerWave[]) => {
  const qrsWaves = waves.filter((wave) => wave.type === 'QRS');

  const closestWavesToOnset = minBy(qrsWaves, (wave) =>
    Math.abs(wave.onset - beat.onset),
  );

  const currentIndex = qrsWaves.findIndex((wave) => {
    return wave.onset === closestWavesToOnset?.onset;
  });

  const currentRR = qrsWaves[currentIndex - 1]?.onset
    ? beat.onset - qrsWaves[currentIndex - 1]?.onset
    : undefined;
  const previousRR =
    qrsWaves[currentIndex - 1]?.onset && qrsWaves[currentIndex - 2]?.onset
      ? qrsWaves[currentIndex - 1]?.onset - qrsWaves[currentIndex - 2]?.onset
      : undefined;
  const nextRR =
    qrsWaves[currentIndex + 1]?.onset && currentIndex !== -1
      ? qrsWaves[currentIndex + 1]?.onset - beat.onset
      : undefined;
  return {
    previousRR,
    currentRR,
    nextRR,
  };
};

export const getSortingStrategy = (
  sortingStrategy: SortingStrategy,
  pinnedBeat: ClusterBeat | undefined,
) => {
  switch (sortingStrategy) {
    case 'RR before':
      return sortByRrBefore;
    case 'RR after':
      return sortByRrAfter;
    case 'Prematurity':
      return sortByPrematurity;
    case 'Compensatory rest':
      return sortByCompensatoryRest;
    case 'Distance to pinned beat':
      if (pinnedBeat == null) {
        return sortByTime;
      }
      return sortByDistanceToPinnedBeat(pinnedBeat, SIDE_POINTS);
    case 'Time':
    default:
      return sortByTime;
  }
};

export const sortByRrBefore = (a: ClusterBeat, b: ClusterBeat) => {
  if (a.currentRR == null && b.currentRR == null) {
    return 0;
  } else if (a.currentRR == null) {
    return 1;
  } else if (b.currentRR == null) {
    return -1;
  } else {
    return a.currentRR - b.currentRR;
  }
};

export const sortByRrAfter = (a: ClusterBeat, b: ClusterBeat) => {
  if (a.nextRR == null && b.nextRR == null) {
    return 0;
  } else if (a.nextRR == null) {
    return 1;
  } else if (b.nextRR == null) {
    return -1;
  } else {
    return a.nextRR - b.nextRR;
  }
};

export const sortByDistanceToPinnedBeat =
  (pinnedBeat: ClusterBeat, onsetIndex: number) =>
  (a: ClusterBeat, b: ClusterBeat) => {
    const pinnedBeatWidthPoints = getNumberOfPointsFromDuration(
      pinnedBeat.offset - pinnedBeat.onset,
    );
    const pinnedBeatTable = pinnedBeat.data.slice(
      onsetIndex,
      onsetIndex + pinnedBeatWidthPoints,
    ) as number[];
    const aTable = a.data.slice(onsetIndex, onsetIndex + pinnedBeatWidthPoints);
    const bTable = b.data.slice(onsetIndex, onsetIndex + pinnedBeatWidthPoints);

    const aDistance = squaredDistance(aTable, pinnedBeatTable);
    const bDistance = squaredDistance(bTable, pinnedBeatTable);

    return aDistance - bDistance;
  };

export const sortByTime = (a: ClusterBeat, b: ClusterBeat) => a.onset - b.onset;

export const sortByPrematurity = (a: ClusterBeat, b: ClusterBeat) => {
  if (a.previousRR == null && b.previousRR == null) {
    return 0;
  } else if (a.previousRR == null || a.currentRR == null) {
    return 1;
  } else if (b.previousRR == null || b.currentRR == null) {
    return -1;
  } else {
    const aPrematurity = a.currentRR / (a.previousRR + a.currentRR);
    const bPrematurity = b.currentRR / (b.previousRR + b.currentRR);

    return aPrematurity - bPrematurity;
  }
};

export const sortByCompensatoryRest = (a: ClusterBeat, b: ClusterBeat) => {
  if (a.nextRR == null && b.nextRR == null) {
    return 0;
  } else if (a.nextRR == null || a.currentRR == null) {
    return 1;
  } else if (b.nextRR == null || b.currentRR == null) {
    return -1;
  } else {
    const aCompensatoryRest = a.currentRR / (a.nextRR + a.currentRR);
    const bCompensatoryRest = b.currentRR / (b.nextRR + b.currentRR);

    return aCompensatoryRest - bCompensatoryRest;
  }
};

type EmptyKey = 'noFilteredClusters' | 'noNoiseMorphologies' | 'default';

export type EmptyValue = { primary: string; secondary: string };

export const emptyMessageContent: Record<EmptyKey, EmptyValue> = {
  noFilteredClusters: {
    primary: 'There is no cluster with this classification',
    secondary: 'Please select another classification',
  },
  noNoiseMorphologies: {
    primary: 'You cannot merge Noise in a morphology',
    secondary: '',
  },
  default: {
    primary: '',
    secondary: '',
  },
};
